/**
 * Ask Indie generated files: CSV cards and table export menus inside answers,
 * Excel / PDF / slides builds with polling, and the inline file preview that the
 * Files tab reuses. Backend: OrbitGeneratedFilesController.
 */
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Check, Copy, Download, Eye, FileSpreadsheet, FileText, Loader2, Lock, Maximize2, Minimize2, Presentation, Trash2, Users, X } from 'lucide-react';
import Request from '../../services/Request';
import type { ChatMessage, OrbitExportTables } from './indie-chat.types';
import type { ExtraProps } from 'react-markdown';

// ─── API ─────────────────────────────────────────────────────────────────────

const filesBase = (clientId?: number | null) => `/api/clients/${clientId}/orbit/generated-files`;

/** Ask Indie generated files (CSV / Excel / PDF / slides) — OrbitFilesController's generated-files routes. */
export const OrbitGeneratedFilesService = {
  list: (clientId?: number | null, conversationId?: string) =>
    Request.get(filesBase(clientId), conversationId ? { conversation_id: conversationId } : undefined, false, true),
  storeCsv: (clientId: number | undefined, data: Record<string, unknown>) => Request.post(`${filesBase(clientId)}/csv`, data),
  build: (clientId: number | undefined, data: Record<string, unknown>) => Request.post(`${filesBase(clientId)}/build`, data),
  show: (clientId: number | null | undefined, id: number) => Request.get(`${filesBase(clientId)}/${id}`, undefined, false, true),
  /** File bytes as a Blob; preview=true returns the PDF rendering of a slides file. */
  content: (clientId: number | null | undefined, id: number, preview = false) =>
    Request.download(`${filesBase(clientId)}/${id}/content`, preview ? { preview: 1 } : {}),
  setVisibility: (clientId: number | null | undefined, id: number, visibility: string) =>
    Request.patch(`${filesBase(clientId)}/${id}/visibility`, { visibility }),
  delete: (clientId: number | null | undefined, id: number) => Request._delete(`${filesBase(clientId)}/${id}`),
};

/** Still in progress: queued ('building') or claimed by the build task ('running'). */
export const isBuilding = (status?: string | null): boolean => status === 'building' || status === 'running';

// ─── Types ───────────────────────────────────────────────────────────────────

// Slides are not buildable any more; existing pptx rows are still listed (see FORMAT_LABEL).
export type BuildFormat = 'xlsx' | 'pdf';
export type Visibility = 'private' | 'shared';

export interface OrbitGeneratedFile {
  id: number;
  title: string;
  filename: string;
  file_format: string;
  source: 'upload' | 'generated';
  visibility: Visibility;
  /** 'running': the build task has claimed the row; still in progress. */
  status: 'building' | 'running' | 'ready' | 'error';
  error_message?: string | null;
  file_size: number;
  file_size_label?: string | null;
  conversation_id?: string | null;
  source_message_id?: number | null;
  has_preview: boolean;
  is_owner: boolean;
  created_at?: string | null;
  download_url?: string;
}

export type SaveArtifactOverrides = {
  title?: string;
  artifact_type?: string;
  content_markdown?: string;
  source_message_id?: number | null;
  /** The generated file this artifact points at */
  orbit_file_id?: number;
};

/** The file summary the artifacts API serializes on each artifact. */
export type ArtifactFile = Pick<OrbitGeneratedFile, 'id' | 'title' | 'filename' | 'file_format' | 'status'> & {
  error_message?: string | null;
  client_id?: number | null;
  download_url?: string;
};

/** What the markdown overrides (pre / table) need to know about the message they render. */
export interface IndieMessageContextValue {
  message: ChatMessage;
  clientId?: number;
  conversationId?: string;
  visibility?: Visibility;
  /** The message's own "Save as artifact" (shared with its action row, so both show one state). */
  save?: MessageSave;
}

export interface MessageSave {
  state: 'idle' | 'saving' | 'saved';
  label: string;
  error?: string | null;
  run: () => void;
}

export const IndieMessageContext = createContext<IndieMessageContextValue | null>(null);

// ─── Refresh event (chat → Files tab) ────────────────────────────────────────

const FILES_CHANGED_EVENT = 'orbit-generated-files-changed';

export function notifyGeneratedFilesChanged(): void {
  window.dispatchEvent(new Event(FILES_CHANGED_EVENT));
}

export function useGeneratedFilesChanged(callback: () => void): void {
  useEffect(() => {
    window.addEventListener(FILES_CHANGED_EVENT, callback);
    return () => window.removeEventListener(FILES_CHANGED_EVENT, callback);
  }, [callback]);
}

// ─── CSV helpers ─────────────────────────────────────────────────────────────

/** RFC 4180-style parse: quoted fields, "" escapes, commas and newlines inside quotes. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i += 1; }
      else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field); field = '';
      if (row.some((cell) => cell !== '')) rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.some((cell) => cell !== '')) rows.push(row);
  return rows;
}

export function toCsv(rows: unknown[][]): string {
  return rows
    .map((row) => row.map((cell) => {
      const value = cell === null || cell === undefined ? '' : String(cell);
      return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
    }).join(','))
    .join('\n');
}

/** ```csv blocks, and untagged blocks whose first line is a comma-separated header. */
export function isCsvBlock(lang: string, text: string): boolean {
  if (lang.toLowerCase() === 'csv') return true;
  if (lang) return false;
  const lines = text.trim().split(/\r?\n/);
  return lines.length > 1 && /^[^,]+(,[^,]+)+$/.test(lines[0].trim());
}

/** First CSV fenced block in a markdown message, with the offset where it starts. */
export function findCsvBlock(markdown: string): { csv: string; offset: number } | null {
  const fence = /```([\w-]*)[^\n]*\n([\s\S]*?)```/g;
  let match: RegExpExecArray | null;
  while ((match = fence.exec(markdown)) !== null) {
    if (isCsvBlock(match[1], match[2])) return { csv: match[2].replace(/\s+$/, ''), offset: match.index };
  }
  return null;
}

/** Nearest markdown heading before `offset`. */
export function headingBefore(markdown: string, offset: number): string | null {
  const headings = Array.from(markdown.slice(0, offset).matchAll(/^#{1,6}\s+(.+?)\s*#*\s*$/gm));
  const last = headings[headings.length - 1];
  return last ? last[1].replace(/[*_`]/g, '').trim() || null : null;
}

export function sourceMessageId(message: ChatMessage): number | undefined {
  const match = /^hist-(\d+)$/.exec(String(message.id));
  return match ? Number(match[1]) : undefined;
}

/** Numbers stay numbers in Excel; codes with leading zeros stay text. */
function coerceCell(value: string): string | number {
  return /^-?(0|[1-9]\d*)(\.\d+)?$/.test(value.trim()) ? Number(value) : value;
}

function fileName(title: string, ext: string): string {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
  return `${slug || 'indie-export'}.${ext}`;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function readBlob(blob: Blob, as: 'text' | 'arraybuffer'): Promise<string | ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string | ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    if (as === 'text') reader.readAsText(blob);
    else reader.readAsArrayBuffer(blob);
  });
}

function sourceFields(ctx: IndieMessageContextValue, withMessageId = true) {
  const id = withMessageId ? sourceMessageId(ctx.message) : undefined;
  return {
    ...(ctx.conversationId ? { conversation_id: ctx.conversationId } : {}),
    ...(id ? { source_message_id: id } : {}),
    ...(ctx.visibility ? { visibility: ctx.visibility } : {}),
  };
}

// ─── Auto-store CSVs ─────────────────────────────────────────────────────────

/** The answer's CSV block, else its main export table (the vendor split when there is one) as CSV. */
export function answerCsv(message: ChatMessage): { csv: string; offset: number } | null {
  const block = findCsvBlock(message.content || '');
  if (block) return block;
  const tables = message.export_tables?.tables ?? [];
  const table = tables.find((t) => t.name === 'Vendor Split') ?? tables[0];
  return table?.columns?.length ? { csv: toCsv([table.columns, ...table.rows]), offset: -1 } : null;
}

/**
 * POST each finished assistant CSV (or plan tables) once, so it shows in Files without a click.
 * History messages first check the conversation's files: the same answer stored
 * live had no message id yet, so the backend cannot dedupe it by itself.
 */
export function useAutoStoreCsv(
  messages: ChatMessage[],
  opts: { clientId?: number; conversationId?: string; visibility?: Visibility; streamingMessageId?: string | null },
): void {
  const done = useRef<Set<string>>(new Set());
  const { clientId, conversationId, visibility, streamingMessageId } = opts;

  useEffect(() => {
    if (!clientId || !conversationId) return;
    const pending = messages
      .filter((m) => m.role === 'assistant' && m.id !== streamingMessageId && !m.metadata?.orbitErrorCode)
      .map((m) => ({ message: m, block: answerCsv(m), key: `${conversationId}|${m.id}` }))
      .filter((item) => item.block && !done.current.has(item.key));
    if (pending.length === 0) return;
    pending.forEach((item) => done.current.add(item.key));

    const titleOf = (m: ChatMessage, offset: number) =>
      (offset >= 0 && headingBefore(m.content, offset)) || m.export_tables?.title || 'Indie export';

    void (async () => {
      let existing: OrbitGeneratedFile[] = [];
      if (pending.some((item) => sourceMessageId(item.message))) {
        try {
          existing = (await OrbitGeneratedFilesService.list(clientId, conversationId))?.data ?? [];
        } catch {
          existing = [];
        }
      }
      await Promise.all(pending.map(async ({ message, block }) => {
        const id = sourceMessageId(message);
        const title = titleOf(message, block!.offset);
        const already = id && existing.some((f) => f.file_format === 'csv' && f.source === 'generated'
          && (f.source_message_id === id || (f.source_message_id == null && f.title === title)));
        if (already) return;
        try {
          await OrbitGeneratedFilesService.storeCsv(clientId, {
            title,
            csv: block!.csv,
            conversation_id: conversationId,
            ...(id ? { source_message_id: id } : {}),
            ...(visibility ? { visibility } : {}),
          });
          notifyGeneratedFilesChanged();
        } catch {
          // not fatal: the card still offers a client-side download
        }
      }));
    })();
  }, [clientId, conversationId, messages, streamingMessageId, visibility]);
}

// ─── Build + poll ────────────────────────────────────────────────────────────

const POLL_MS = 3000;

export function useGeneratedFileBuild(clientId?: number) {
  const [file, setFile] = useState<OrbitGeneratedFile | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      window.clearTimeout(timer.current);
    };
  }, []);

  const poll = useCallback((id: number, delay: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      try {
        const res = await OrbitGeneratedFilesService.show(clientId, id);
        if (!alive.current) return;
        const next = res?.data as OrbitGeneratedFile | undefined;
        if (!next) throw new Error('missing');
        setFile(next);
        if (isBuilding(next.status)) poll(id, POLL_MS);
        else notifyGeneratedFilesChanged();
      } catch {
        if (alive.current) setError('Lost track of this file. It will still appear in the Files tab.');
      }
    }, delay);
  }, [clientId]);

  const start = useCallback(async (body: Record<string, unknown>) => {
    if (!clientId) return;
    window.clearTimeout(timer.current);
    setError(null);
    setFile(null);
    setPending(true);
    try {
      const res = await OrbitGeneratedFilesService.build(clientId, body);
      if (!alive.current) return;
      const created = res?.data as OrbitGeneratedFile;
      setFile(created);
      notifyGeneratedFilesChanged();
      // a reused ready file comes back without its signed link: fetch it now
      if (created.status !== 'error') poll(created.id, created.status === 'ready' ? 0 : POLL_MS);
    } catch {
      if (alive.current) setError('Could not start the build. Please try again.');
    } finally {
      if (alive.current) setPending(false);
    }
  }, [clientId, poll]);

  return { file, pending, error, start };
}

// ─── Save an answer as a file-backed artifact ────────────────────────────────

type ExportTable = OrbitExportTables['tables'][number];

const SUMMARY_MAX = 100000;

function splitRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').replace(/\\\|/g, '\u0000').split('|')
    .map((cell) => cell.replace(/\u0000/g, '|').replace(/\*\*|__|`/g, '').trim());
}

/** GFM tables in a markdown answer, each with the char span it covers. */
export function markdownTables(markdown: string): Array<ExportTable & { length: number; offset: number }> {
  const lines = markdown.split('\n');
  const tables: Array<ExportTable & { length: number; offset: number }> = [];
  let offset = 0;
  for (let i = 0; i < lines.length; i += 1) {
    const start = offset;
    offset += lines[i].length + 1;
    if (!lines[i].includes('|') || !/^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(lines[i + 1] ?? '')) continue;
    const columns = splitRow(lines[i]);
    const rows: Array<Array<string | number>> = [];
    let length = lines[i].length + lines[i + 1].length + 2;
    offset += lines[i + 1].length + 1;
    i += 2;
    while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
      rows.push(splitRow(lines[i]).map(coerceCell));
      length += lines[i].length + 1;
      offset += lines[i].length + 1;
      i += 1;
    }
    i -= 1;
    tables.push({ name: (headingBefore(markdown, start) || `Table ${tables.length + 1}`).slice(0, 100), columns, rows, length, offset: start });
  }
  return tables;
}

/** Tables for an Excel build: export_tables first, else markdown tables plus the CSV block. */
export function messageTables(message: ChatMessage): ExportTable[] {
  if (message.export_tables?.tables?.length) return message.export_tables.tables;
  const tables: ExportTable[] = markdownTables(message.content).map(({ name, columns, rows }) => ({ name, columns, rows }));
  const block = findCsvBlock(message.content);
  if (block) {
    const [columns = [], ...rows] = parseCsv(block.csv);
    tables.push({ name: (headingBefore(message.content, block.offset) || 'CSV').slice(0, 100), columns, rows: rows.map((r) => r.map(coerceCell)) });
  }
  return tables;
}

/** Excel for table-heavy answers, PDF for text-heavy ones. */
export function pickArtifactFormat(message: ChatMessage): 'xlsx' | 'pdf' {
  if (message.export_tables?.tables?.length || findCsvBlock(message.content)) return 'xlsx';
  const tables = markdownTables(message.content);
  if (tables.some((t) => t.rows.length >= 5)) return 'xlsx';
  const tableChars = tables.reduce((sum, t) => sum + t.length, 0);
  return tables.length > 0 && tableChars > message.content.length / 2 ? 'xlsx' : 'pdf';
}

export function messageTitle(message: ChatMessage): string {
  const firstHeading = /^#{1,6}\s+(.+?)\s*#*\s*$/m.exec(message.content)?.[1].replace(/[*_`]/g, '').trim();
  const firstLine = message.content.replace(/[#*_`>]/g, '').trim().split('\n')[0]?.trim();
  return (message.export_tables?.title || firstHeading || firstLine || 'Indie answer').slice(0, 120);
}

/** POST /build for an artifact: xlsx with tables, or pdf from the answer markdown. Does not wait for the build. */
export async function startArtifactFileBuild(opts: {
  clientId: number;
  format: 'xlsx' | 'pdf';
  title: string;
  tables?: ExportTable[];
  summary?: string;
  fields?: Record<string, unknown>;
}): Promise<OrbitGeneratedFile> {
  const body = opts.format === 'xlsx'
    ? { format: 'xlsx', title: opts.title, tables: opts.tables ?? [], ...opts.fields }
    : { format: 'pdf', title: opts.title, summary: (opts.summary ?? '').slice(0, SUMMARY_MAX), ...opts.fields };
  const res = await OrbitGeneratedFilesService.build(opts.clientId, body);
  const file = res?.data as OrbitGeneratedFile | undefined;
  if (!file?.id) throw new Error('Build did not return a file');
  notifyGeneratedFilesChanged();
  return file;
}

/** Signed link when available, else the authenticated bytes. */
export async function downloadGeneratedFile(clientId: number | undefined, file: { id: number; filename?: string }): Promise<void> {
  const res = await OrbitGeneratedFilesService.show(clientId, file.id);
  const url = res?.data?.download_url;
  if (url) {
    window.open(url, '_blank', 'noopener');
    return;
  }
  downloadBlob(await OrbitGeneratedFilesService.content(clientId, file.id), res?.data?.filename || file.filename || 'indie-file');
}

/** Keeps a file's status current: polls GET /{id} every 3 s while it is building. */
export function useLiveGeneratedFile<T extends { id: number; status: string }>(file: T | null | undefined, clientId?: number | null): T | null {
  const [live, setLive] = useState<T | null>(file ?? null);
  useEffect(() => { setLive(file ?? null); }, [file]);
  const id = live?.id;
  const building = isBuilding(live?.status);
  useEffect(() => {
    if (!id || !building || !clientId) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const next = (await OrbitGeneratedFilesService.show(clientId, id))?.data;
        if (!cancelled && next) {
          setLive((prev) => (prev ? { ...prev, ...next } : prev));
          if (!isBuilding(next.status)) notifyGeneratedFilesChanged();
        }
      } catch {
        // try again on the next tick
        if (!cancelled) setLive((prev) => (prev ? { ...prev } : prev));
      }
    }, POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [id, building, clientId, live]);
  return live;
}

const FORMAT_LABEL: Record<string, string> = { xlsx: 'Excel', pdf: 'PDF', pptx: 'Slides', csv: 'CSV' };

const BuildResult: React.FC<{
  clientId?: number;
  file: OrbitGeneratedFile | null;
  pending: boolean;
  error: string | null;
}> = ({ clientId, file, pending, error }) => {
  if (error) return <p className="mt-2 text-[12px] text-[#B42318]">{error}</p>;
  if (pending || isBuilding(file?.status)) {
    return (
      <div className="mt-2 flex items-center gap-2 text-[12px] text-[#6941C6]" role="status">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Building {file ? FORMAT_LABEL[file.file_format] ?? file.file_format : 'file'}… about a minute</span>
      </div>
    );
  }
  if (!file) return null;
  if (file.status === 'error') {
    return <p className="mt-2 text-[12px] text-[#B42318]">{file.error_message || 'The file could not be built.'}</p>;
  }
  return (
    <div className="mt-2 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-medium text-[#344054]">{file.filename}</span>
        {file.download_url && (
          <a
            href={file.download_url}
            download={file.filename}
            className="inline-flex items-center gap-1 rounded-[8px] bg-[#7F56D9] px-2.5 py-1 text-[11px] font-medium text-white hover:bg-[#6941C6]"
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </a>
        )}
      </div>
      {clientId && file.has_preview !== false && <GeneratedFilePreview clientId={clientId} file={file} />}
    </div>
  );
};

// ─── Inline preview ──────────────────────────────────────────────────────────

const PREVIEW_ROWS = 50;

type PreviewState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'none' }
  | { kind: 'pdf'; url: string }
  | { kind: 'sheets'; sheets: Array<{ name: string; rows: unknown[][] }> };

export const GeneratedFilePreview: React.FC<{ clientId: number; file: Pick<OrbitGeneratedFile, 'id' | 'title' | 'file_format'> }> = ({ clientId, file }) => {
  const [state, setState] = useState<PreviewState>({ kind: 'loading' });
  const [sheetIndex, setSheetIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const format = file.file_format;

  useEffect(() => {
    let cancelled = false;
    let url: string | undefined;
    setState({ kind: 'loading' });
    if (!['pdf', 'pptx', 'xlsx', 'csv'].includes(format)) {
      setState({ kind: 'none' });
      return undefined;
    }
    (async () => {
      const blob: Blob = await OrbitGeneratedFilesService.content(clientId, file.id, format === 'pptx');
      if (cancelled) return;
      if (format === 'pdf' || format === 'pptx') {
        url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setState({ kind: 'pdf', url });
      } else if (format === 'xlsx') {
        const book = XLSX.read(await readBlob(blob, 'arraybuffer'), { type: 'array' });
        if (cancelled) return;
        setState({
          kind: 'sheets',
          sheets: book.SheetNames.map((name) => ({
            name,
            rows: XLSX.utils.sheet_to_json<unknown[]>(book.Sheets[name], { header: 1, defval: '' }),
          })),
        });
      } else {
        const rows = parseCsv(String(await readBlob(blob, 'text')));
        if (!cancelled) setState({ kind: 'sheets', sheets: [{ name: 'CSV', rows }] });
      }
    })().catch(() => { if (!cancelled) setState({ kind: 'error' }); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [clientId, file.id, format]);

  if (state.kind === 'loading') {
    return (
      <div className="flex items-center gap-2 py-3 text-[12px] text-[#98A2B3]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading preview…
      </div>
    );
  }
  if (state.kind === 'error') return <p className="py-2 text-[12px] text-[#B42318]">Preview unavailable.</p>;
  if (state.kind === 'none') return <p className="py-2 text-[12px] text-[#98A2B3]">No preview for this file type.</p>;

  if (state.kind === 'pdf') {
    return (
      <div className="rounded-[10px] border border-[#EAECF0] bg-white">
        <div className="flex justify-end border-b border-[#EAECF0] px-2 py-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'Collapse preview' : 'Expand preview'}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-[#667085] hover:bg-[#F9F5FF] hover:text-[#6941C6]"
          >
            {expanded ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
        <iframe
          title={`${file.title} preview`}
          src={state.url}
          className="block w-full rounded-b-[10px]"
          style={{ height: expanded ? '80vh' : 480 }}
        />
      </div>
    );
  }

  const sheet = state.sheets[Math.min(sheetIndex, state.sheets.length - 1)] ?? { name: '', rows: [] };
  const [header = [], ...body] = sheet.rows;
  return (
    <div className="rounded-[10px] border border-[#EAECF0] bg-white">
      {state.sheets.length > 1 && (
        <div className="flex gap-1 overflow-x-auto border-b border-[#EAECF0] px-2 pt-1.5" role="tablist">
          {state.sheets.map((s, i) => (
            <button
              key={s.name}
              type="button"
              role="tab"
              aria-selected={i === sheetIndex}
              onClick={() => setSheetIndex(i)}
              className={`shrink-0 border-b-2 px-2 pb-1 text-[11px] font-medium ${i === sheetIndex ? 'border-[#6941C6] text-[#6941C6]' : 'border-transparent text-[#667085]'}`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
      <PreviewTable header={header} rows={body.slice(0, PREVIEW_ROWS)} />
      {body.length > PREVIEW_ROWS && (
        <p className="border-t border-[#EAECF0] px-3 py-1.5 text-[11px] text-[#667085]">
          {(body.length - PREVIEW_ROWS).toLocaleString()} more rows
        </p>
      )}
    </div>
  );
};

const PreviewTable: React.FC<{ header: unknown[]; rows: unknown[][]; expanded?: boolean }> = ({ header, rows, expanded = false }) => (
  <div className="overflow-auto" style={{ maxHeight: expanded ? '75vh' : 420 }} data-testid="indie-table-scroll">
    <table className="min-w-full border-collapse text-[11px]">
      <thead className="sticky top-0 bg-[#F9F5FF]">
        <tr>
          {header.map((cell, i) => (
            <th key={i} className="whitespace-nowrap border-b border-[#EAECF0] px-2 py-1 text-left font-semibold text-[#53389E]">{String(cell ?? '')}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, r) => (
          <tr key={r} className="odd:bg-white even:bg-[#FCFAFF]">
            {header.map((_, c) => (
              <td key={c} className="whitespace-nowrap border-b border-[#F2F4F7] px-2 py-1 text-[#344054]">{String(row[c] ?? '')}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Shared button styles ────────────────────────────────────────────────────

const btn = 'inline-flex items-center gap-1 rounded-[8px] border border-[#D0D5DD] bg-white px-2.5 py-1 text-[11px] font-medium text-[#344054] hover:bg-[#F9F5FF] hover:border-[#D6BBFB] hover:text-[#6941C6] disabled:opacity-50 disabled:cursor-not-allowed';

export const BUILD_BUTTONS: Array<{ format: BuildFormat; label: string; Icon: React.ElementType }> = [
  { format: 'xlsx', label: 'Excel', Icon: FileSpreadsheet },
  { format: 'pdf', label: 'PDF', Icon: FileText },
];

// ─── Tall tables ─────────────────────────────────────────────────────────────

/** Tables with more data rows than this scroll inside a fixed-height box. */
export const TALL_TABLE_ROWS = 12;

const linkBtn = 'inline-flex items-center gap-1 rounded-[6px] px-1.5 py-0.5 text-[11px] font-medium text-[#6941C6] hover:bg-[#F9F5FF]';

const TallTableBar: React.FC<{
  rows: number;
  expanded: boolean;
  onToggle: () => void;
  onDownloadCsv: () => void;
  children?: React.ReactNode;
}> = ({ rows, expanded, onToggle, onDownloadCsv, children }) => (
  <div className="flex flex-wrap items-center gap-1.5 py-1 text-[11px] text-[#667085]" data-testid="indie-tall-table-bar">
    <span className="mr-auto">{rows.toLocaleString()} rows · scroll to see more, or download</span>
    <button type="button" className={linkBtn} onClick={onDownloadCsv}>
      <Download className="h-3 w-3" /> Download CSV
    </button>
    {children}
    <button type="button" className={linkBtn} onClick={onToggle} aria-expanded={expanded}>
      {expanded ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
      {expanded ? 'Collapse' : 'Expand'}
    </button>
  </div>
);

// ─── CSV card ────────────────────────────────────────────────────────────────

export const CsvCard: React.FC<{ csv: string; title: string; ctx: IndieMessageContextValue }> = ({ csv, title, ctx }) => {
  const rows = useMemo(() => parseCsv(csv), [csv]);
  const [header = [], ...body] = rows;
  const build = useGeneratedFileBuild(ctx.clientId);
  const [copied, setCopied] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [tall, setTall] = useState(false);
  const exportTables = ctx.message.export_tables;
  const downloadCsv = () => downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), fileName(title, 'csv'));

  const cardTables = () => (exportTables?.tables?.length
    ? exportTables.tables
    : [{ name: title, columns: header, rows: body.map((row) => row.map(coerceCell)) }]);

  const startBuild = (format: BuildFormat) => {
    void build.start({ format, title: exportTables?.title || title, tables: cardTables(), ...sourceFields(ctx) });
  };

  const copy = () => {
    navigator.clipboard.writeText(csv).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => undefined);
  };

  return (
    <div className="my-2 rounded-[12px] border border-[#E9D7FE] bg-white" data-testid="indie-csv-card">
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-[#F4EBFF] px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-[#101828]">{title}</p>
          <p className="text-[11px] text-[#667085]">
            {body.length.toLocaleString()} rows · {header.length} columns
          </p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-[#F4EBFF] px-2 py-0.5 text-[10px] font-medium text-[#6941C6]">
          <FileSpreadsheet className="h-3 w-3" /> CSV
        </span>
      </div>
      {showAll ? (
        <>
          <div className="px-3">
            <TallTableBar rows={body.length} expanded={tall} onToggle={() => setTall((v) => !v)} onDownloadCsv={downloadCsv}>
              <button type="button" className={linkBtn} onClick={() => { setShowAll(false); setTall(false); }}>Show less</button>
            </TallTableBar>
          </div>
          <PreviewTable header={header} rows={body} expanded={tall} />
        </>
      ) : (
        <>
          <PreviewTable header={header} rows={body.slice(0, 5)} />
          {body.length > 5 && (
            <p className="flex items-center gap-2 px-3 py-1 text-[11px] text-[#98A2B3]">
              Showing 5 of {body.length.toLocaleString()} rows
              <button type="button" className={linkBtn} onClick={() => setShowAll(true)}>Show all</button>
            </p>
          )}
        </>
      )}
      <div className="flex flex-wrap items-center gap-1.5 border-t border-[#F4EBFF] px-3 py-2">
        <button type="button" className={btn} onClick={downloadCsv}>
          <Download className="h-3.5 w-3.5" /> Download CSV
        </button>
        {ctx.clientId ? BUILD_BUTTONS.map(({ format, label, Icon }) => (
          <button key={format} type="button" className={btn} disabled={build.pending || isBuilding(build.file?.status)} onClick={() => startBuild(format)}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        )) : null}
        <button type="button" className={btn} onClick={copy} aria-label="Copy CSV">
          {copied ? <Check className="h-3.5 w-3.5 text-[#027A48]" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        {ctx.save && (
          <button type="button" className={btn} onClick={ctx.save.run} disabled={ctx.save.state !== 'idle'}>
            {ctx.save.state === 'saving' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {ctx.save.state === 'saved' && <Check className="h-3.5 w-3.5 text-[#027A48]" />}
            {ctx.save.label}
          </button>
        )}
      </div>
      {ctx.save?.error && <p className="px-3 pb-2 text-[12px] text-[#B42318]" role="alert">{ctx.save.error}</p>}
      {(build.pending || build.file || build.error) && (
        <div className="px-3 pb-3">
          <BuildResult clientId={ctx.clientId} {...build} />
        </div>
      )}
    </div>
  );
};

// ─── Table export ────────────────────────────────────────────────────────────

function readTable(table: HTMLTableElement): { columns: string[]; rows: string[][] } {
  const all = Array.from(table.querySelectorAll('tr')).map((tr) =>
    Array.from(tr.querySelectorAll('th,td')).map((cell) => (cell.textContent ?? '').trim()));
  const [columns = [], ...rows] = all;
  return { columns, rows };
}

export const TableExportMenu: React.FC<{
  ctx: IndieMessageContextValue;
  getTable: () => { columns: string[]; rows: string[][] } | null;
  title: string;
  build: ReturnType<typeof useGeneratedFileBuild>;
}> = ({ ctx, getTable, title, build }) => {
  const [open, setOpen] = useState(false);
  const exportTables = ctx.message.export_tables;

  const exportCsv = () => {
    setOpen(false);
    const table = getTable();
    if (table) downloadBlob(new Blob([toCsv([table.columns, ...table.rows])], { type: 'text/csv;charset=utf-8' }), fileName(title, 'csv'));
  };

  const exportBuild = (format: BuildFormat) => {
    setOpen(false);
    if (exportTables?.tables?.length) {
      void build.start({ format, title: exportTables.title || title, tables: exportTables.tables, ...sourceFields(ctx) });
      return;
    }
    const table = getTable();
    if (!table) return;
    // one table of several in the answer: no message id, or the backend would hand back another table's file
    void build.start({
      format,
      title,
      tables: [{ name: title, columns: table.columns, rows: table.rows.map((row) => row.map(coerceCell)) }],
      ...sourceFields(ctx, false),
    });
  };

  return (
    <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          className={linkBtn}
        >
          <Download className="h-3 w-3" /> Export
        </button>
        {open && (
          <div role="menu" className="absolute right-0 top-full z-20 mt-1 w-32 rounded-[8px] border border-[#EAECF0] bg-white py-1 shadow-lg">
            <button type="button" role="menuitem" onClick={exportCsv} className="block w-full px-3 py-1.5 text-left text-[12px] text-[#344054] hover:bg-[#F9F5FF]">CSV</button>
            {ctx.clientId ? BUILD_BUTTONS.map(({ format, label }) => (
              <button key={format} type="button" role="menuitem" onClick={() => exportBuild(format)} className="block w-full px-3 py-1.5 text-left text-[12px] text-[#344054] hover:bg-[#F9F5FF]">{label}</button>
            )) : null}
          </div>
        )}
    </div>
  );
};

// ─── Workbook panel ──────────────────────────────────────────────────────────

/**
 * One Excel workbook for the whole answer (a tab per table) plus its Save as artifact.
 * The workbook is built by the server (the same build as the Files tab, so totals and column
 * formats apply): this starts it and shows its state. Nothing is assembled in the browser.
 */
export const WorkbookPanel: React.FC<{ ctx: IndieMessageContextValue }> = ({ ctx }) => {
  const build = useGeneratedFileBuild(ctx.clientId);
  // before the early return (hooks order); a poll tick re-renders this panel with the same message
  const tables = useMemo(() => messageTables(ctx.message).filter((t) => t.columns.length), [ctx.message]);
  const tabs = tables.map((t) => t.name);
  if (!tabs.length) return null;
  const startWorkbook = () => {
    void build.start({ format: 'xlsx', title: messageTitle(ctx.message), tables, ...sourceFields(ctx) });
  };
  return (
    <div className="mb-3 rounded-[12px] border border-[#D6BBFB] bg-[#F9F5FF] px-3 py-2.5" data-testid="indie-workbook-panel">
      <div className="flex items-start gap-2.5">
        <FileSpreadsheet className="mt-0.5 h-5 w-5 shrink-0 text-[#6941C6]" aria-hidden />
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-[#101828]">Get this as an Excel workbook</p>
          <p className="text-[11px] text-[#667085]">
            {tabs.length === 1 ? 'One tab: ' : `${tabs.length} tabs, one per list: `}{tabs.join(', ')}
          </p>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 pl-[30px]">
        {ctx.clientId ? (
          <button
            type="button"
            onClick={startWorkbook}
            disabled={build.pending || isBuilding(build.file?.status)}
            className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#7F56D9] px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm hover:bg-[#6941C6] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" /> Download Excel
          </button>
        ) : null}
        {ctx.save && (
          <button type="button" className={btn} onClick={ctx.save.run} disabled={ctx.save.state !== 'idle'}>
            {ctx.save.state === 'saving' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {ctx.save.state === 'saved' && <Check className="h-3.5 w-3.5 text-[#027A48]" />}
            {ctx.save.label}
          </button>
        )}
      </div>
      {ctx.save?.error && <p className="mt-1.5 pl-[30px] text-[12px] text-[#B42318]" role="alert">{ctx.save.error}</p>}
      {(build.pending || build.file || build.error) && (
        <div className="pl-[30px]">
          <BuildResult clientId={ctx.clientId} {...build} />
        </div>
      )}
    </div>
  );
};

// ─── react-markdown overrides ────────────────────────────────────────────────
// Module-level so React keeps card state (builds in progress) across re-renders.

type HastNode = { type?: string; value?: string; properties?: { className?: unknown }; children?: HastNode[]; position?: { start?: { offset?: number } } };

export const MarkdownPre: React.FC<JSX.IntrinsicElements['pre'] & ExtraProps> = ({ node, children, ...rest }) => {
  const ctx = useContext(IndieMessageContext);
  const hast = node as unknown as HastNode | undefined;
  const code = hast?.children?.[0];
  const className = code?.properties?.className;
  const lang = Array.isArray(className) ? String(className[0] ?? '').replace(/^language-/, '') : '';
  const text = (code?.children ?? []).map((child) => child.value ?? '').join('').replace(/\s+$/, '');
  if (ctx && ctx.message.role === 'assistant' && isCsvBlock(lang, text)) {
    const offset = hast?.position?.start?.offset ?? ctx.message.content.length;
    const title = headingBefore(ctx.message.content, offset) || ctx.message.export_tables?.title || 'Indie export';
    return <CsvCard csv={text} title={title} ctx={ctx} />;
  }
  return <pre {...rest}>{children}</pre>;
};

export const MarkdownTable: React.FC<JSX.IntrinsicElements['table'] & ExtraProps> = ({ node, children, ...rest }) => {
  const ctx = useContext(IndieMessageContext);
  const ref = useRef<HTMLTableElement>(null);
  const build = useGeneratedFileBuild(ctx?.clientId);
  const [rowCount, setRowCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  useLayoutEffect(() => {
    setRowCount(ref.current?.querySelectorAll('tbody tr').length ?? 0);
  }, [children]);
  const tall = rowCount > TALL_TABLE_ROWS;
  const getTable = () => (ref.current ? readTable(ref.current) : null);
  const offset = (node as unknown as HastNode | undefined)?.position?.start?.offset;
  const title = ctx
    ? headingBefore(ctx.message.content, offset ?? ctx.message.content.length) || ctx.message.export_tables?.title || 'Indie table'
    : 'Indie table';
  const isAnswer = Boolean(ctx && ctx.message.role === 'assistant');
  // the workbook panel sits once, above the answer's first table
  const firstTable = isAnswer && (offset === undefined
    || !markdownTables(ctx!.message.content).some((t) => t.offset < offset - 2));
  const exportMenu = isAnswer ? <TableExportMenu ctx={ctx!} title={title} getTable={getTable} build={build} /> : null;
  const downloadCsv = () => {
    const table = getTable();
    if (table) downloadBlob(new Blob([toCsv([table.columns, ...table.rows])], { type: 'text/csv;charset=utf-8' }), fileName(title, 'csv'));
  };
  return (
    <div className="indie-table-block my-2">
      {firstTable && <WorkbookPanel ctx={ctx!} />}
      {tall ? (
        <TallTableBar rows={rowCount} expanded={expanded} onToggle={() => setExpanded((v) => !v)} onDownloadCsv={downloadCsv}>
          {exportMenu}
        </TallTableBar>
      ) : exportMenu && <div className="flex flex-wrap items-center justify-end gap-1.5">{exportMenu}</div>}
      {(build.pending || build.file || build.error) && <BuildResult clientId={ctx?.clientId} {...build} />}
      <div
        className={tall ? 'indie-table-scroll overflow-auto rounded-[8px] border border-[#EAECF0]' : 'overflow-x-auto'}
        style={tall ? { maxHeight: expanded ? '75vh' : 420 } : undefined}
      >
        <table ref={ref} {...rest}>{children}</table>
      </div>
    </div>
  );
};

// ─── Files tab ───────────────────────────────────────────────────────────────

/**
 * Files tab: Ask Indie generated files (and uploads) for the current client,
 * grouped by conversation with the current one first.
 */
interface FilesTabProps {
  clientId?: number;
  conversationId?: string;
  /** Hide uploads (the V3 drawer lists them separately). */
  generatedOnly?: boolean;
}

function formatIcon(format: string) {
  if (format === 'csv' || format === 'xlsx') return <FileSpreadsheet className="h-3.5 w-3.5 text-[#079455]" />;
  if (format === 'pptx') return <Presentation className="h-3.5 w-3.5 text-[#B54708]" />;
  return <FileText className="h-3.5 w-3.5 text-[#6941C6]" />;
}

function dateLabel(value?: string | null): string {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime())
    ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';
}

const iconBtn = 'flex h-[22px] w-[22px] items-center justify-center rounded-[5px] text-[#98A2B3] hover:bg-[#F4EBFF] hover:text-[#6941C6] disabled:opacity-40 disabled:cursor-not-allowed';

export const IndieGeneratedFilesTab: React.FC<FilesTabProps> = ({ clientId, conversationId, generatedOnly = false }) => {
  const [files, setFiles] = useState<OrbitGeneratedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState<OrbitGeneratedFile | null>(null);

  const load = useCallback(async () => {
    if (!clientId) {
      setFiles([]);
      setLoading(false);
      return;
    }
    try {
      const res = await OrbitGeneratedFilesService.list(clientId);
      setFiles(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);
  useGeneratedFilesChanged(load);

  // A build runs as a Cloud Task: follow each building row with GET /{id} every 3 s until it settles.
  const buildingIds = files.filter((f) => isBuilding(f.status)).map((f) => f.id).join(',');
  useEffect(() => {
    if (!buildingIds || !clientId) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const updates = await Promise.all(buildingIds.split(',').map(async (id) => {
        try {
          return (await OrbitGeneratedFilesService.show(clientId, Number(id)))?.data as OrbitGeneratedFile | undefined;
        } catch {
          return undefined; // try again on the next tick
        }
      }));
      if (cancelled) return;
      const byId = new Map(updates.filter((u): u is OrbitGeneratedFile => Boolean(u)).map((u) => [u.id, u]));
      // a new array even with no change re-arms the timer for rows still building
      setFiles((prev) => prev.map((f) => (byId.has(f.id) ? { ...f, ...byId.get(f.id) } : f)));
    }, POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [buildingIds, clientId, files]);

  const groups = useMemo(() => {
    const visible = files
      .filter((f) => !generatedOnly || f.source === 'generated')
      .sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')));
    const byConversation = new Map<string, OrbitGeneratedFile[]>();
    visible.forEach((f) => {
      const key = f.conversation_id ?? '';
      byConversation.set(key, [...(byConversation.get(key) ?? []), f]);
    });
    return Array.from(byConversation.entries())
      .sort(([a], [b]) => Number(b === conversationId) - Number(a === conversationId))
      .map(([key, items]) => ({
        key,
        label: key && key === conversationId
          ? 'This conversation'
          : key ? `Conversation · ${dateLabel(items[0].created_at)}` : 'Not in a conversation',
        items,
      }));
  }, [conversationId, files, generatedOnly]);

  const download = async (file: OrbitGeneratedFile) => {
    try {
      await downloadGeneratedFile(clientId, file);
    } catch {
      window.alert('Unable to download this file right now.');
    }
  };

  const toggleVisibility = async (file: OrbitGeneratedFile) => {
    const visibility = file.visibility === 'shared' ? 'private' : 'shared';
    try {
      const res = await OrbitGeneratedFilesService.setVisibility(clientId, file.id, visibility);
      setFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, ...(res?.data ?? { visibility }) } : f)));
    } catch {
      window.alert('Unable to change who sees this file right now.');
    }
  };

  const remove = async (file: OrbitGeneratedFile) => {
    if (!window.confirm(`Delete "${file.title}"? This cannot be undone.`)) return;
    try {
      await OrbitGeneratedFilesService.delete(clientId, file.id);
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
    } catch {
      window.alert('Unable to delete this file right now.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-[#98A2B3]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span className="text-[11px]">Loading files…</span>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center gap-1 px-2 py-8 text-center">
        <FileSpreadsheet className="mb-1 h-5 w-5 text-[#D0D5DD]" />
        <p className="text-[11px] font-medium text-[#98A2B3]">No files yet</p>
        <p className="text-[10px] leading-[14px] text-[#C0C6D1]">
          CSVs Indie writes, and Excel, PDF or slides you export, appear here.
        </p>
      </div>
    );
  }

  return (
    <>
      {groups.map((group) => (
        <div key={group.key || 'none'} className="mb-4 last:mb-0">
          <span className="mb-[9px] block px-[6px] text-[9px] font-semibold uppercase leading-[13.5px] tracking-[0.977px] text-[#98A2B3]">
            {group.label}
          </span>
          <div className="flex flex-col gap-[4px]">
            {group.items.map((file) => {
              const ready = file.status === 'ready';
              return (
                <div key={file.id} className="flex items-start gap-2 rounded-[10px] border border-[#EAECF0] bg-[#F9FAFB] p-[8px_9px]" data-testid="generated-file-row">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] bg-white">{formatIcon(file.file_format)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-medium leading-[16px] text-[#101828]" title={file.title}>{file.title}</p>
                    <p className="flex flex-wrap items-center gap-1 text-[10px] leading-[15px] text-[#98A2B3]">
                      <span>{[file.file_format.toUpperCase(), file.file_size_label, dateLabel(file.created_at)].filter(Boolean).join(' · ')}</span>
                      {file.visibility === 'shared' && (
                        <span className="rounded-full bg-[#EFF8FF] px-1.5 text-[9px] font-medium text-[#1570EF]">Shared</span>
                      )}
                    </p>
                    {isBuilding(file.status) && (
                      <p className="flex items-center gap-1 text-[10px] text-[#6941C6]"><Loader2 className="h-3 w-3 animate-spin" /> Building…</p>
                    )}
                    {file.status === 'error' && (
                      <p className="text-[10px] text-[#B42318]">{file.error_message || 'Build failed'}</p>
                    )}
                    <div className="mt-1 flex items-center gap-[2px]">
                      <button type="button" className={iconBtn} aria-label={`Preview ${file.title}`} title="Preview" disabled={!ready || !file.has_preview} onClick={() => setPreviewFile(file)}>
                        <Eye className="h-3 w-3" />
                      </button>
                      <button type="button" className={iconBtn} aria-label={`Download ${file.title}`} title="Download" disabled={!ready} onClick={() => void download(file)}>
                        <Download className="h-3 w-3" />
                      </button>
                      {file.is_owner && (
                        <>
                          <button
                            type="button"
                            className="ml-auto inline-flex items-center gap-1 rounded-[5px] px-1 py-0.5 text-[10px] text-[#667085] hover:bg-[#F4EBFF] hover:text-[#6941C6]"
                            onClick={() => void toggleVisibility(file)}
                            title={file.visibility === 'shared' ? 'Make private' : 'Share with team'}
                          >
                            {file.visibility === 'shared' ? <Users className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                            {file.visibility === 'shared' ? 'Shared with team' : 'Only me'}
                          </button>
                          <button type="button" className={`${iconBtn} hover:!bg-[#FEE4E2] hover:!text-[#D92D20]`} aria-label={`Delete ${file.title}`} title="Delete" onClick={() => void remove(file)}>
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {previewFile && clientId && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[#101828]/40 p-4" onClick={() => setPreviewFile(null)}>
          <div
            role="dialog"
            aria-label={`${previewFile.title} preview`}
            className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-[20px] border border-[#EAECF0] bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-[#EAECF0] px-5 py-3">
              <p className="truncate text-[15px] font-semibold text-[#101828]">{previewFile.title}</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void download(previewFile)}
                  className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#D0D5DD] px-3 py-1.5 text-[11px] font-medium text-[#344054] hover:bg-[#F9FAFB]"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewFile(null)}
                  aria-label="Close file preview"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-[#98A2B3] hover:bg-[#F9FAFB]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <GeneratedFilePreview clientId={clientId} file={previewFile} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
